import { defineStore } from "pinia";
import axios from "@shared/axios";
import ApiLoginInterface from "@shared-interfaces/account/api.login.interface";
import { computed, ref } from "vue";
import useStateStore from "@shared-stores/state.store";
import { resetAllSilentRefresh } from "@shared-composables/useSilentRefresh";
import { resetAllAsyncResources } from "@shared-composables/useAsyncResource";
import { resetWorkoutsPeriodCache } from "@shared-composables/workout/useWorkoutsPeriod";
import { isApp, hasSessionHint } from "@shared-utils/system.utils";

const useAuthStore = defineStore(
	"auth",
	() =>
	{
		const token = ref<string>("");
		const tokenParts = ref<Record<string, any>>([]);
		const refreshToken = ref<string>("");
		const expiredTime = ref(null);
		const referer = ref<string | undefined>(undefined);

		const isLoggedIn = computed(() => !! token.value && ! isTokenExpired());
		const getReferer = computed(() => referer.value);

		let initPromise: Promise<void> | null = null;
		let refreshPromise: Promise<string | null> | null = null;

		async function init()
		{
			if (initPromise)
			{
				return initPromise;
			}

			// Un refresh empêché par le réseau ne doit ni casser le boot ni déconnecter : on garde
			// l'état tel quel, la prochaine requête retentera.
			initPromise = _doInit().catch(() => {});
			await initPromise;
		}

		async function _doInit()
		{
			// Web/admin : token volatil (pas de persist) → au boot, tenter un silent refresh via le
			// cookie HttpOnly, SEULEMENT si le hint de session est présent (sinon anonyme → aucun appel).
			// Natif : token persisté ; token absent = pas de session (pas de cookie).
			if (token.value === "" && ! isApp())
			{
				if (! hasSessionHint())
				{
					resetWithState();
					return;
				}

				const newToken = await refreshAndGetNewToken();

				if (newToken)
				{
					parseAndSetToken(newToken);
				}
				else
				{
					resetWithState();
				}

				return;
			}

			if (token.value !== "" && isTokenExpired())
			{
				const newToken = await refreshAndGetNewToken();

				if (newToken)
				{
					parseAndSetToken(newToken);
				}
				else
				{
					resetWithState();
				}
			}
			else if (! (token.value !== "" && isValidToken()))
			{
				resetWithState();
			}
		}

		function resetWithState(): void
		{
			reset(true);

			const stateStore = useStateStore();
			stateStore.user = null;
		}

		async function login(email: string, password: string, rememberMe = false): Promise<ApiLoginInterface>
		{
			reset(true);

			const response = await axios.post(
				"/auth",
				{
					"email": email,
					"password": password,
					// Web/admin : pilote la persistance du cookie refresh (30 j vs session). Natif : ignoré.
					"remember_me": rememberMe
				},
				{
					headers: {
						"Authorization": ""
					}
				}
			);

			return response?.data as ApiLoginInterface ?? {};
		}

		async function loginAsIsabelle(): Promise<ApiLoginInterface>
		{
			return await login("isabelle@crossfit-limouzi.fr", "zumfec-varpik-9xyZvo");
		}

		function logout(): void
		{
			// Invalide le refresh token côté serveur (natif : body ; web : cookie) + purge le cookie.
			// Fire-and-forget : ne pas bloquer la déconnexion sur la réponse réseau.
			axios.post(
				"/auth/logout",
				isApp() ? { "refresh_token": refreshToken.value } : {},
				{ headers: { "Authorization": "" } }
			).catch(() => {});

			reset();
			resetAllSilentRefresh();
			resetAllAsyncResources();
			resetWorkoutsPeriodCache();
		}

		/**
		 * Un seul rafraîchissement à la fois, quel qu'en soit le demandeur.
		 *
		 * `init()` au démarrage et l'intercepteur axios sur un 401 rafraîchissaient chacun de leur
		 * côté : le verrou de l'intercepteur ne connaît pas l'appel d'`init()`. Deux requêtes pour
		 * un seul besoin, et deux résultats concurrents écrits dans le même store.
		 */
		async function refreshAndGetNewToken(): Promise<string | null>
		{
			if (refreshPromise)
			{
				return refreshPromise;
			}

			refreshPromise = doRefreshAndGetNewToken()
				.finally(() =>
				{
					refreshPromise = null;
				});

			return refreshPromise;
		}

		async function doRefreshAndGetNewToken(): Promise<string | null>
		{
			// Natif : refresh token dans le body (guard sur sa présence).
			// Web/admin : refresh token dans le cookie HttpOnly (envoyé via withCredentials), body vide.
			if (isApp() && ! refreshToken.value)
			{
				return null;
			}

			try
			{
				const response = await axios.post(
					"/token/refresh",
					isApp() ? { "refresh_token": refreshToken.value } : {},
					{
						headers: {
							"content-type": "application/ld+json",
							"accept": "application/ld+json",
							"Authorization": ""
						}
					}
				);

				if (response && response?.data)
				{
					return response?.data?.token ?? null;
				}
			}
			catch (error: any)
			{
				// Seul un refus d'authentification invalide la session. Une panne réseau ou un 500
				// ne disent rien des jetons : déconnecter dessus éjecterait un adhérent dont la
				// session est valide, au premier hoquet de réseau.
				const status = error?.response?.status ?? error?.code;

				if (status !== 401 && status !== 403)
				{
					throw error;
				}

				reset();
			}

			return null;
		}

		function parseAndSetToken(data: string): void
		{
			try
			{
				if (data && data.trim() != "")
				{
					const parts = data.split(".");

					if (parts.length === 3)
					{
						// Décoder le payload en base64
						const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
						const payloadString = decodeURIComponent(
							atob(payloadBase64)
								.split("")
								.map(c => `%${ ("00" + c.charCodeAt(0).toString(16)).slice(-2) }`)
								.join("")
						);

						token.value = data;
						tokenParts.value = JSON.parse(payloadString);
						expiredTime.value = tokenParts.value["exp"];
						return;
					}
				}
			}
			catch (e)
			{
			}

			clearToken();
		}

		async function refreshParseAndSetToken(): Promise<void>
		{
			const newToken = await refreshAndGetNewToken();

			if (newToken)
			{
				parseAndSetToken(newToken);
			}
		}

		function setReferer(route: string): void
		{
			if (! route.startsWith("/admin/auth"))
			{
				referer.value = route;
			}
		}

		function isValidToken(): boolean
		{
			return token.value !== "" && ! isTokenExpired();
		}

		function isTokenExpired(): boolean
		{
			const now = Date.now() / 1000; // Convertir en secondes
			return now > (expiredTime.value ?? 0);
		}

		function reset(keepReferer = false): void
		{
			clearToken();
			clearRefreshToken();

			if (!keepReferer)
			{
				clearReferer();
			}
		}

		function clearToken(): void
		{
			token.value = "";
			tokenParts.value = [];
			expiredTime.value = null;
		}

		function clearRefreshToken(): void
		{
			refreshToken.value = "";
		}

		function clearReferer(): void
		{
			referer.value = "";
		}

		return {
			token,
			tokenParts,
			refreshToken,
			expiredTime,
			referer,

			isLoggedIn,
			getReferer,

			init,
			login,
			loginAsIsabelle,
			logout,
			clearToken,
			refreshAndGetNewToken,
			parseAndSetToken,
			refreshParseAndSetToken,
			setReferer,
			isValidToken,
			isTokenExpired
		};
	},
	{
		// Natif : token/refreshToken persistés (Preferences). Web/admin : mémoire seule
		// (token volatil, refresh token en cookie HttpOnly) → rien de sensible en localStorage.
		// @ts-ignore
		persist: isApp() ? { enabled: true } : false
	}
);

export default useAuthStore;
